import {
  appThemes,
  BridgeLogo,
  CircleLogo,
  HabitatLogo,
  PulseLogo,
} from "@pex/design-tokens";
import { Globe, MapPin } from "lucide-react";
import type { AppItem } from "../components/navigation/app-switcher";

// Branded SVG logos for first-party apps. Website and Finder use Lucide icons
// (Globe, MapPin) tinted with the theme primary instead of a branded mark.
const APP_LOGO_COMPONENTS = {
  bridge: BridgeLogo,
  pulse: PulseLogo,
  circle: CircleLogo,
  habitat: HabitatLogo,
} as const;

const APP_ICON_COMPONENTS = {
  website: Globe,
  finder: MapPin,
} as const;

const CORE_APPS = [
  "website",
  "finder",
  "bridge",
  "pulse",
  "circle",
  "habitat",
] as const;

type CoreAppKey = (typeof CORE_APPS)[number];

const PUBLIC_APPS = new Set<CoreAppKey>(["website", "finder"]);

function hasLogo(
  appKey: CoreAppKey
): appKey is keyof typeof APP_LOGO_COMPONENTS {
  return appKey in APP_LOGO_COMPONENTS;
}

function hasIcon(
  appKey: CoreAppKey
): appKey is keyof typeof APP_ICON_COMPONENTS {
  return appKey in APP_ICON_COMPONENTS;
}

/**
 * Returns AppItem configs for all core apps. The app matching `currentApp`
 * is included with `isActive: true` so the switcher can mark it as the
 * current location (rather than hiding it).
 */
export function getAppConfigs(currentApp?: string): AppItem[] {
  return CORE_APPS.map((appKey) => {
    const theme = appThemes[appKey];
    return {
      name: theme.name,
      // `url` only exists on app themes that are real destinations.
      href: "url" in theme ? theme.url : "#",
      color: theme.primary,
      group: PUBLIC_APPS.has(appKey) ? "public" : "gated",
      ...(hasLogo(appKey) && { LogoComponent: APP_LOGO_COMPONENTS[appKey] }),
      ...(hasIcon(appKey) && { icon: APP_ICON_COMPONENTS[appKey] }),
      isActive: appKey === currentApp,
    };
  });
}

/**
 * Export commonly used icons
 */
export { Users } from "lucide-react";
